import React, { useState } from 'react';
import { useTranslation } from 'utils/i18n';
import { ProviderCard } from './ProviderCard';
import { ProviderDetailModal } from './ProviderDetailModal';
import marketplaceTranslations from './translations';

const PROVIDERS_PER_PAGE = 12;

/**
 * Marketplace provider grid component with pagination
 * Displays providers in a grid layout
 */
export const ProviderGrid = ({
  providers,
  loading = false,
  selectedProvider,
  onProviderSelect,
  products = [],
  onProductSelect = null,
  filters = {},
  viewModeToggle = null
}) => {
  const { language } = useTranslation();
  const t = (key, defaultValue = '') => {
    const keys = key.split('.');
    let value = marketplaceTranslations[language] || marketplaceTranslations.en;
    for (const k of keys) {
      value = value?.[k];
    }
    return value || defaultValue || key;
  };
  const [currentPage, setCurrentPage] = useState(1);
  const [providerProducts, setProviderProducts] = useState({}); // Cache: providerId -> products with tags
  const [loadingProviderProducts, setLoadingProviderProducts] = useState(false);
  
  // Load products for all providers to check tags
  React.useEffect(() => {
    if (providers.length > 0 && filters.selectedTags && filters.selectedTags.length > 0) {
      loadProviderProducts();
    }
  }, [providers.length, filters.selectedTags?.length]);
  
  const loadProviderProducts = async () => {
    try {
      setLoadingProviderProducts(true);
      // NOTE: Must stay within src/ for CRA builds
      const apiClient = await import('utils/apiClient').then(m => m.getApiClient());
      const productsCache = {};
      
      for (const provider of providers) {
        try {
          // Use new public marketplace API (no authentication required)
          const productsResponse = await apiClient.get(`/api/v1/marketplace/products/${provider.id}`);
          
          if (productsResponse.success && productsResponse.products) {
            productsCache[provider.id] = productsResponse.products;
          } else {
            productsCache[provider.id] = [];
          }
        } catch (err) {
          console.warn(`Failed to load products for provider ${provider.id}:`, err);
          productsCache[provider.id] = [];
        }
      }
      
      setProviderProducts(productsCache);
    } catch (err) {
      console.error('Error loading provider products:', err);
    } finally {
      setLoadingProviderProducts(false);
    }
  };
  
  // Hardcoded B-esper provider (always first) - memoized to avoid re-renders
  const besperProvider = React.useMemo(() => ({
    id: 'besper',
    name: 'B-esper',
    description: t('providerGrid.besperDescription', 'B2B SaaS marketplace and hosting platform. Discover, deploy, and scale enterprise AI solutions.'),
    isBesper: true, // Flag to identify B-esper provider
    metadata: {
      partner_profile: {
        description: t('providerGrid.besperFullDescription', 'B-esper is your B2B SaaS marketplace and hosting platform. We provide infrastructure and platform services, offering packaged and raw infrastructure to customers and partners.'),
        logo_url: null
      },
      tags: ['infrastructure', 'marketplace', 'ai', 'enterprise', 'cloud', 'platform']
    }
  }), [t]);

  // Filter providers based on search and tags
  const filteredProviders = React.useMemo(() => {
    // Start with other providers (not B-esper)
    let filtered = [...providers];
    
    // Filter by search query
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      filtered = filtered.filter(provider => 
        provider.name?.toLowerCase().includes(query) ||
        provider.description?.toLowerCase().includes(query) ||
        (provider.metadata?.partner_profile?.description || '').toLowerCase().includes(query)
      );
    }
    
    // Filter by tags (both provider-level tags and product tags)
    if (filters.selectedTags && filters.selectedTags.length > 0) {
      filtered = filtered.filter(provider => {
        // 1. Check provider-level tags from tenant metadata
        const providerTags = (provider.metadata?.tags || []).map(t => 
          typeof t === 'string' ? t.trim().toLowerCase() : String(t).toLowerCase()
        ).filter(Boolean);
        
        const hasProviderTag = filters.selectedTags.some(selectedTag => 
          providerTags.includes(selectedTag.toLowerCase())
        );
        
        if (hasProviderTag) {
          return true; // Provider matches via provider-level tags
        }
        
        // 2. Check product tags from Stripe products
        const providerProds = providerProducts[provider.id] || [];
        const hasProductTag = providerProds.some(product => {
          if (!product.metadata?.tags) return false;
          const productTags = product.metadata.tags.split(',').map(t => t.trim().toLowerCase());
          return filters.selectedTags.some(selectedTag => 
            productTags.includes(selectedTag.toLowerCase())
          );
        });
        
        return hasProductTag; // Provider matches via product tags
      });
    }
    
    // B-esper always shown first, regardless of filters
    // This is a hardcoded provider that must always be visible
    return [besperProvider, ...filtered];
  }, [providers, filters.searchQuery, filters.selectedTags, providerProducts, besperProvider]);
  
  // Calculate pagination on filtered providers
  const totalPages = Math.ceil(filteredProviders.length / PROVIDERS_PER_PAGE);
  const startIndex = (currentPage - 1) * PROVIDERS_PER_PAGE;
  const endIndex = startIndex + PROVIDERS_PER_PAGE;
  const paginatedProviders = filteredProviders.slice(startIndex, endIndex);
  
  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [filters.searchQuery, filters.selectedTags?.length]);
  
  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Scroll to top of grid
    const gridElement = document.querySelector('.marketplace-grid');
    if (gridElement) {
      gridElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };
  
  const handlePrevious = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };
  
  const handleNext = () => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  };
  
  if (loading) {
    return (
      <div className="marketplace-grid-section">
        <div className="marketplace-results-header">
          <div className="marketplace-results-count">
            <span className="skeleton-text" style={{ width: '150px', height: '20px' }}></span>
          </div>
        </div>
        <div className="marketplace-grid loading">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="marketplace-card skeleton">
              <div className="skeleton-header"></div>
              <div className="skeleton-body"></div>
              <div className="skeleton-footer"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  if (filteredProviders.length === 0 && !loading && !loadingProviderProducts) {
    return (
      <div className="marketplace-grid-section">
        <div className="marketplace-results-header">
          <div className="marketplace-results-count">
            <span>{t('empty.noProviders', 'No providers found')}</span>
          </div>
        </div>
        <div className="marketplace-grid empty">
          <div className="empty-state">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
            <h3>{t('empty.noProviders', 'No providers found')}</h3>
            <p>{t('empty.noProvidersMessage', 'No providers are currently visible on the marketplace.')}</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <>
      <div className="marketplace-grid-section">
        {/* Results Header */}
        <div className="marketplace-results-header">
          <div className="marketplace-results-count">
            <span className="results-count-text">
              {t('pagination.showing', 'Showing')} {startIndex + 1}-{Math.min(endIndex, filteredProviders.length)} {t('pagination.of', 'of')} {filteredProviders.length} {t('pagination.providers', 'providers')}
            </span>
          </div>
          {totalPages > 1 && (
            <div className="marketplace-pagination-info">
              Page {currentPage} of {totalPages}
            </div>
          )}
          {viewModeToggle && <div style={{ marginLeft: 'auto' }}>{viewModeToggle}</div>}
        </div>
        
        {/* Provider Grid */}
        <div className="marketplace-grid">
          {paginatedProviders.map(provider => (
            <ProviderCard
              key={provider.id}
              provider={provider}
              isSelected={selectedProvider?.id === provider.id}
              onSelect={onProviderSelect}
              loading={loading}
            />
          ))}
        </div>
        
        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="marketplace-pagination">
            <button
              className="pagination-button pagination-prev"
              onClick={handlePrevious}
              disabled={currentPage === 1}
              aria-label="Previous page"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 18l-6-6 6-6"/>
              </svg>
              {t('buttons.previous', 'Previous')}
            </button>
            
            <div className="pagination-pages">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                // Show first page, last page, current page, and pages around current
                if (
                  page === 1 ||
                  page === totalPages ||
                  (page >= currentPage - 1 && page <= currentPage + 1)
                ) {
                  return (
                    <button
                      key={page}
                      className={`pagination-page ${currentPage === page ? 'active' : ''}`}
                      onClick={() => handlePageChange(page)}
                      aria-label={`Go to page ${page}`}
                    >
                      {page}
                    </button>
                  );
                } else if (page === currentPage - 2 || page === currentPage + 2) {
                  return <span key={page} className="pagination-ellipsis">...</span>;
                }
                return null;
              })}
            </div>
            
            <button
              className="pagination-button pagination-next"
              onClick={handleNext}
              disabled={currentPage === totalPages}
              aria-label="Next page"
            >
              {t('buttons.next', 'Next')}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 18l6-6-6-6"/>
              </svg>
            </button>
          </div>
        )}
      </div>

      {/* Provider Detail Modal */}
      {selectedProvider && (
        <ProviderDetailModal
          provider={selectedProvider}
          products={products}
          onClose={() => onProviderSelect(null)}
          onProductSelect={onProductSelect}
        />
      )}
    </>
  );
};

